import { useEffect, useState } from "react";

import { BsLink45Deg } from "react-icons/bs";
import classNames from "classnames";

import ReadMore from "../../components/common/ReadMore";
import TextNavBackground from "../../components/common/TextNavBackground";
import PlayerAvatar from "../../components/elements/players/PlayerAvatar";
import PlayerBioStats from "../../components/elements/players/PlayerBioStats";
import PlayerCardStyle from "../../components/elements/players/PlayerCardStyle";
import PlayerStats from "../../components/elements/players/PlayerStats";
import { apiWrapper } from "../../utils/api/apiWrapper";
import {
  kKotokoPlayerDetailURL,
  kKotokoPlayersLegendsListURL,
} from "../../utils/constants/apiUrls";
import { convertToShortMonth } from "../../utils/helpers/dateAndTime";
import {
  customSeoConfig,
  getPlayerGroup,
  getPlayerPosition,
  getApiToken,
} from "../../utils/helpers/helpers";
import SocialShareIcons from "../../components/common/icons/SocialShareIcons";
import CopyToClipboard from "react-copy-to-clipboard";
import WebShareIcon from "../../components/common/icons/WebShareIcon";
import NotifyToastContainer, {
  notify,
} from "../../components/common/NotifyToastContainer";
import { NoDataFullPage } from "../../components/common/NoData";
import { TWELVE_HOURS_REFRESH_INTERVAL_SECONDS } from "../../utils/constants/numbers";



const LegendProfile = ({ player }) => {
  // 1. EARLY CRASH PROTECTION: If player object is malformed, bypass rendering entirely
  if (!player || !player.player_first_name) {
    return <NoDataFullPage />;
  }

  // Safe defaults extraction
  const {
    public_url: playerPublicUrl = "",
    player_position: playerPosition = "",
    player_country_name: playerCountryName = "Unknown",
    player_age: playerAge = "-",
    player_appearances: playerAppearances = "-",
    player_clean_sheet: playerCleanSheet = "-",
    player_goals_scored: playerGoalsScored = "-",
    player_first_name: playerFirstName = "",
    player_last_name: playerLastName = "",
    player_biography: playerBiography = "",
    player_picture: playerPicture = "",
    player_status: playerStatus = "",
    player_country_code: playerCountryCode = ""
  } = player;

  const [playerDateOfBirth, setPlayerDateOfBirth] = useState("");
  const [playerJoinedDate, setplayerJoinedDate] = useState("");
  const [value, setValue] = useState(playerPublicUrl);
  const [copied, setCopied] = useState(false);

  // SAFE FIX: Safe argument execution wrappers
  const playerGroup = typeof getPlayerGroup === 'function' ? getPlayerGroup(player) : "-";
  const playerPositionFormatted = typeof getPlayerPosition === 'function' ? getPlayerPosition(playerPosition || "") : "Player";

  const stats = [
    { label: "Country", value: playerCountryName },
    { label: "Date of birth", value: playerDateOfBirth },
    { label: "Joined", value: playerJoinedDate },
  ];

  const bioStat = [
    { label: "Age", value: playerAge },
    { label: "Appearance", value: playerAppearances },
    playerPosition === "GOALKEEPER" || playerPosition === "Goalkeeper"
      ? { label: "Clean sheets", value: playerCleanSheet }
      : { label: "Goals scored", value: playerGoalsScored },
    { label: "Group", value: playerGroup },
  ];

  const playerFullName = `${playerFirstName} ${playerLastName}`.trim() || "Kotoko Legend";
  const playerBioCaption = (playerBiography || "").slice(0, 120);
  const playerSeoTitle = `${playerFullName} | ${playerPositionFormatted}`;

  useEffect(() => {
    if (player?.player_date_of_birth) {
      setPlayerDateOfBirth(convertToShortMonth(player.player_date_of_birth));
    }
    if (player?.player_joined_date) {
      setplayerJoinedDate(convertToShortMonth(player.player_joined_date));
    }
  }, [player?.player_date_of_birth, player?.player_joined_date]);

  return (
    <>
      {customSeoConfig(
        playerSeoTitle,
        playerBioCaption,
        playerPicture,
        playerPublicUrl,
        "website"
      )}

      <TextNavBackground title={playerFullName} />

      <div className="relative py-16 sm:py-10">
        <div className="lg:mx-auto lg:max-w-7xl lg:px-8 lg:grid lg:grid-cols-2 lg:gap-24 lg:items-start">
          <div className="relative sm:py-16 lg:py-0">
            <PlayerCardStyle />

            <PlayerAvatar
              playerAvatar={playerPicture}
              playerFullName={playerFullName}
              playerPosition={playerPosition}
              playerStatus={playerStatus}
            />
          </div>

          <div className="relative mx-auto max-w-md px-6 sm:max-w-3xl lg:px-0">
            <div className="pt-12 sm:pt-16 lg:pt-20">
              <div className="flex justify-center items-center space-x-2 mb-4">
                <SocialShareIcons
                  title={playerFullName}
                  caption={playerBioCaption}
                  url={playerPublicUrl}
                />

                <CopyToClipboard
                  text={value}
                  onCopy={() => {
                    setCopied({ copied: true });
                    setValue(playerPublicUrl);
                  }}
                >
                  <BsLink45Deg
                    onClick={notify}
                    size={30}
                    className={classNames(
                      copied ? "text-green-600" : "",
                      "cursor-pointer text-gray-500 hover:text-black"
                    )}
                  />
                </CopyToClipboard>

                <WebShareIcon
                  title={playerFullName}
                  caption={playerBioCaption}
                  url={playerPublicUrl}
                />
              </div>

              <PlayerBioStats playerBioStats={bioStat} />

              <div className="mt-14 text-gray-500 space-y-6">
                <h1 className="text-base font-medium text-gray-500 border-b-2">
                  {"Biography"}
                </h1>

                <ReadMore>{playerBiography}</ReadMore>
              </div>
            </div>

            <PlayerStats
              stats={stats}
              countryCode={playerCountryCode}
            />
          </div>
        </div>
      </div>
      {copied ? <NotifyToastContainer /> : null}
    </>
  );
};

export default LegendProfile;

export const getStaticProps = async (context) => {
  const token = getApiToken();
  const playerSlug = context.params.slug;

  let playerData = null;

  try {
    const player = await apiWrapper.get(
      kKotokoPlayerDetailURL + `?slug=${playerSlug}`,
      token
    );
    playerData = player?.data || null;
  } catch (error) {
    console.error(`Error loading profile: ${playerSlug}`, error);
  }

  // CRITICAL PROTECTION LAYER: If the profile metadata is missing key requirements, force a 404
  if (!playerData || !playerData.player_first_name || Object.keys(playerData).length === 0) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      player: playerData,
    },
    revalidate: TWELVE_HOURS_REFRESH_INTERVAL_SECONDS,
  };
};

export async function getStaticPaths() {
  const token = getApiToken();
  const players = await apiWrapper.get(kKotokoPlayersLegendsListURL, token);
  const data = players?.data;

  const kotokoLegends = [
    ...(data?.goalkeepers || []),
    ...(data?.defenders || []),
    ...(data?.midfielders || []),
    ...(data?.attackers || []),
  ];

  const paths = kotokoLegends
    .filter((player) => player && player.slug)
    .map((player) => ({
      params: { slug: player.slug },
    }));

  return { paths, fallback: "blocking" };
}
